import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserRound, Loader2, Sparkles, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const empty = {
  name: "", age: "", household: "alleen", cities: "", max_price: 2000, min_m2: 30, bedrooms: 1,
  pets: false, move_date: "", income: "", property_type: "appartement", furnished: "egaal",
  phone: "", contact_email: "", work_status: "", intro_text: "",
};

const Section = ({ title, children }) => (
  <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
    <h2 className="font-display text-lg font-bold text-navy">{title}</h2>
    <div className="mt-4 space-y-4">{children}</div>
  </div>
);

const Profiel = () => {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const isPremiumUser = user?.is_premium || (user?.subscription_tier && user.subscription_tier !== "free");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const manageSubscription = async () => {
    setPortalBusy(true);
    try {
      const res = await api("/billing/create-portal-session", {
        method: "POST",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (res.url) window.location.href = res.url;
      else toast.error(t("p2.profiel.subError"));
    } catch (e) {
      toast.error(e.message || t("p2.profiel.subError"));
    } finally {
      setPortalBusy(false);
    }
  };

  useEffect(() => {
    api("/profile").then((p) => {
      if (p) setForm({ ...empty, ...p, cities: (p.cities || []).join(", "), age: p.age ?? "", income: p.income ?? "", move_date: p.move_date ?? "" });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("p2.profiel.errName"));
    setBusy(true);
    try {
      const body = {
        ...form,
        age: form.age ? Number(form.age) : null,
        income: form.income ? Number(form.income) : null,
        max_price: Number(form.max_price), min_m2: Number(form.min_m2), bedrooms: Number(form.bedrooms),
        cities: form.cities.split(",").map((c) => c.trim()).filter(Boolean),
        move_date: form.move_date || null,
      };
      const res = await api("/profile", { method: "POST", body: JSON.stringify(body) });
      toast.success(t("p2.profiel.saved", { n: res.estimated_weekly_matches }));
      navigate("/dashboard");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" size={32} /></div>;

  return (
    <div>
      <PageHeader icon={UserRound} title={t("p2.profiel.title")} subtitle={t("p2.profiel.subtitle")} />
      <div className="mx-auto max-w-2xl space-y-5 px-4 py-8 sm:px-6">
        <Section title={t("p2.profiel.about")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("common.name")}</Label><Input className="mt-2 rounded-xl" data-testid="profiel-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label>{t("p2.profiel.age")}</Label><Input className="mt-2 rounded-xl" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} /></div>
          </div>
          <div>
            <Label>{t("p2.profiel.household")}</Label>
            <Select value={form.household} onValueChange={(v) => set("household", v)}>
              <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alleen">{t("p2.profiel.hh.alleen")}</SelectItem>
                <SelectItem value="samenwonend">{t("p2.profiel.hh.samen")}</SelectItem>
                <SelectItem value="gezin">{t("p2.profiel.hh.gezin")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("p2.profiel.income")}</Label><Input className="mt-2 rounded-xl" type="number" value={form.income} onChange={(e) => set("income", e.target.value)} /></div>
            <div><Label>{t("p2.profiel.work")}</Label><Input className="mt-2 rounded-xl" value={form.work_status} onChange={(e) => set("work_status", e.target.value)} /></div>
          </div>
        </Section>

        <Section title={t("p2.profiel.what")}>
          <div><Label>{t("p2.profiel.cities")}</Label><Input className="mt-2 rounded-xl" data-testid="profiel-cities" value={form.cities} onChange={(e) => set("cities", e.target.value)} placeholder="Amsterdam, Utrecht" /></div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div><Label>{t("p2.profiel.maxrent")}</Label><Input className="mt-2 rounded-xl" type="number" value={form.max_price} onChange={(e) => set("max_price", e.target.value)} /></div>
            <div><Label>{t("woningen.f.minm2")}</Label><Input className="mt-2 rounded-xl" type="number" value={form.min_m2} onChange={(e) => set("min_m2", e.target.value)} /></div>
            <div><Label>{t("detail.bedrooms")}</Label><Input className="mt-2 rounded-xl" type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{t("p2.profiel.type")}</Label>
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="appartement">{t("p2.profiel.t.appartement")}</SelectItem>
                  <SelectItem value="studio">{t("p2.profiel.t.studio")}</SelectItem>
                  <SelectItem value="huis">{t("p2.profiel.t.huis")}</SelectItem>
                  <SelectItem value="kamer">{t("p2.profiel.t.kamer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("p2.profiel.furnish")}</Label>
              <Select value={form.furnished} onValueChange={(v) => set("furnished", v)}>
                <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="egaal">{t("p2.profiel.f.egaal")}</SelectItem>
                  <SelectItem value="gemeubileerd">{t("p2.profiel.f.gemeubileerd")}</SelectItem>
                  <SelectItem value="ongemeubileerd">{t("p2.profiel.f.ongemeubileerd")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("p2.profiel.movedate")}</Label><Input className="mt-2 rounded-xl" type="date" value={form.move_date} onChange={(e) => set("move_date", e.target.value)} /></div>
            <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
              <Label className="font-medium">{t("p2.profiel.pets")}</Label>
              <Switch checked={form.pets} onCheckedChange={(v) => set("pets", v)} />
            </div>
          </div>
        </Section>

        <Section title={t("p2.profiel.contact")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("p2.profiel.phone")}</Label><Input className="mt-2 rounded-xl" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>{t("p2.profiel.cemail")}</Label><Input className="mt-2 rounded-xl" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></div>
          </div>
          <div>
            <Label>{t("p2.profiel.intro")}</Label>
            <Textarea className="mt-2 min-h-[100px] rounded-xl" value={form.intro_text} onChange={(e) => set("intro_text", e.target.value)} placeholder={t("p2.profiel.introPh")} />
          </div>
        </Section>

        {isPremiumUser && (
          <Section title={t("p2.profiel.subscription")}>
            <p className="text-sm text-slate-600" data-testid="profiel-sub-active">{t("p2.profiel.subActive")}</p>
            <Button
              onClick={manageSubscription}
              disabled={portalBusy}
              variant="outline"
              data-testid="profiel-manage-subscription"
              className="w-full rounded-xl py-6 text-base"
            >
              {portalBusy ? <Loader2 className="animate-spin" size={18} /> : <><CreditCard size={18} className="mr-2" /> {t("p2.profiel.subManage")}</>}
            </Button>
            <p className="text-xs text-slate-400">{t("p2.profiel.subNote")}</p>
          </Section>
        )}

        <Button onClick={save} disabled={busy} data-testid="profiel-save" className="w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <><Sparkles size={18} className="mr-2" /> {t("p2.profiel.save")}</>}
        </Button>
      </div>
    </div>
  );
};

export default Profiel;
